import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "ssm_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const addItem = (product, size, quantity = 1, variant = null) => {
    setItems((prev) => {
      const colorName = variant?.name || null;
      const idx = prev.findIndex(
        (i) =>
          i.product_id === product.id &&
          i.size === size &&
          (i.color || null) === colorName
      );
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], quantity: clone[idx].quantity + quantity };
        return clone;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          size,
          quantity,
          color: colorName,
          image: variant?.images?.[0] || product.images?.[0],
        },
      ];
    });
    setOpen(true);
  };

  const removeItem = (product_id, size, color = null) => {
    setItems((prev) =>
      prev.filter(
        (i) =>
          !(
            i.product_id === product_id &&
            i.size === size &&
            (i.color || null) === color
          )
      )
    );
  };

  const updateQty = (product_id, size, quantity, color = null) => {
    setItems((prev) =>
      prev.map((i) =>
        i.product_id === product_id &&
        i.size === size &&
        (i.color || null) === color
          ? { ...i, quantity: Math.max(1, quantity) }
          : i
      )
    );
  };

  const clear = () => setItems([]);

  const { subtotal, count } = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, count };
  }, [items]);

  const shipping = items.length === 0 ? 0 : subtotal >= 100 ? 0 : 9.0;
  const total = subtotal + shipping;

  const value = {
    items,
    isOpen,
    setOpen,
    addItem,
    removeItem,
    updateQty,
    clear,
    subtotal,
    shipping,
    total,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
